import { io, Socket } from "socket.io-client";
import { InternalSubscription } from "./internalSubscription";
import type {
  BaseMessageHandler,
  Message,
  MessageHandler,
} from "./types/messageDto";
import type { Subscription } from "./types/subscription";
import type { Auth } from "./types/auth";
import { config } from "@/config";
import { getKey } from "./utilities";
import { getToken } from "../auth";
import type { TopicDataMap } from "@repo/types";

const messageServiceUrl = config.urls.messageService;

export class ClientMessageService {
  //#region Properties: Private

  private socket: Socket | null = null;
  private connectionId: string | null = null;
  private internalSubscriptions: Map<string, InternalSubscription> = new Map();

  //#endregion

  //#region EventHandlers: Private

  private readonly onConnect = () => {};

  private readonly onConnected = (data: { connectionId: string }) => {
    if (data.connectionId && data.connectionId !== this.connectionId) {
      this.connectionId = data.connectionId;

      this.getEmptySubscriptions().forEach((sub) => {
        this.unsubscribe(sub);
      });
      this.getNotEmptySubscriptions().forEach((sub) => {
        sub.subscribe();
      });
    } else {
      this.getEmptySubscriptions().forEach((sub) => {
        this.unsubscribe(sub);
      });
      this.getNotConnectedSubscriptions().forEach((sub) => {
        sub.subscribe();
      });
    }
  };

  private readonly onDisconnect = () => {};

  private readonly onConnectError = (error: Error) => {
    console.error("Connection error:", error);

    if (!this.socket?.active) {
      this.detachHandlers();
      this.socket = null;
      this.connectionId = null;

      console.warn(
        "The message service connection is broken. It can only be re-established by logging out and logging back in."
      );
    }
  };

  private readonly onError = (error: Error) => {
    console.error("Socket error:", error);
  };

  private readonly onMessage = (
    message: Message,
    ack?: (response: { status: string; ts?: number }) => void
  ) => {
    const sub = this.getSubscriptionById(message.subscriptionId);
    sub?.handle(message);
    if (ack && typeof ack === "function") {
      ack({ status: "received", ts: Date.now() });
    }
  };

  //#endregion

  //#region Methods: Private

  private readonly getEmptySubscriptions = () => {
    const emptySubscriptions: InternalSubscription[] = [];
    this.internalSubscriptions.forEach((sub) => {
      if (sub.isEmpty()) {
        emptySubscriptions.push(sub);
      }
    });
    return emptySubscriptions;
  };

  private readonly getNotEmptySubscriptions = () => {
    const notEmptySubscriptions: InternalSubscription[] = [];
    this.internalSubscriptions.forEach((sub) => {
      if (!sub.isEmpty()) {
        notEmptySubscriptions.push(sub);
      }
    });
    return notEmptySubscriptions;
  };

  private readonly getNotConnectedSubscriptions = () => {
    const notEmptySubscriptions: InternalSubscription[] = [];
    this.internalSubscriptions.forEach((sub) => {
      if (!sub.isEmpty() && !sub.getSubscriptionId()) {
        notEmptySubscriptions.push(sub);
      }
    });
    return notEmptySubscriptions;
  };

  private getSubscriptionById(
    subscriptionId: string
  ): InternalSubscription | undefined {
    for (const sub of this.internalSubscriptions.values()) {
      if (sub.getSubscriptionId() === subscriptionId) {
        return sub;
      }
    }
    return undefined;
  }

  private async internalSubscribe(
    topic: string,
    keys: string[]
  ): Promise<string | undefined> {
    if (!this.socket || !this.socket.connected) {
      return undefined;
    }
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        resolve(undefined);
      }, 5000);
      this.socket?.emit(
        "subscribe",
        { topic, keys },
        (response: { subscriptionId: string }) => {
          clearTimeout(timeout);
          resolve(response.subscriptionId);
        }
      );
    });
  }

  private unsubscribe = (subscription: InternalSubscription) => {
    if (this.socket?.connected) {
      this.internalSubscriptions.delete(subscription.getKey());
      if (subscription.getSubscriptionId()) {
        this.socket.emit(
          "unsubscribe",
          subscription.getSubscriptionId(),
          (response: string) => {
            if (response !== "unsubscribed") {
              console.error("Failed to unsubscribe:", response);
            }
          }
        );
      }
    }
  };

  private createAuth(token: string): Auth {
    const auth: Auth = { token };
    if (this.connectionId) {
      auth.lastConnectionId = this.connectionId;
    }
    return auth;
  }

  private initHandlers(): void {
    this.socket?.on("connect", this.onConnect);
    this.socket?.on("connected", this.onConnected);
    this.socket?.on("disconnect", this.onDisconnect);
    this.socket?.on("connect_error", this.onConnectError);
    this.socket?.on("error", this.onError);
    this.socket?.on("message", this.onMessage);
  }

  private detachHandlers(): void {
    this.socket?.off("connect", this.onConnect);
    this.socket?.off("connected", this.onConnected);
    this.socket?.off("disconnect", this.onDisconnect);
    this.socket?.off("connect_error", this.onConnectError);
    this.socket?.off("error", this.onError);
    this.socket?.off("message", this.onMessage);
  }

  //#endregion

  //#region Methods: Public

  async connect(): Promise<void> {
    if (this.socket) {
      this.socket.disconnect(); // disconnect old open socket
      this.detachHandlers();
    }

    this.socket = io(messageServiceUrl, {
      auth: async (cb) => {
        const token = await getToken();
        if (!token) {
          console.error("No token found for message service connection.");
          return;
        }
        cb(this.createAuth(token));
      },
      path: "/ws",
    });

    this.initHandlers();
  }

  async disconnect(): Promise<void> {
    this.internalSubscriptions.clear();
    this.socket?.emit("close_connection", {}, (response: string) => {
      if (response !== "closed") {
        console.error("Closing connection error:", response);
      }
      this.socket?.disconnect();
      this.socket = null;
      this.connectionId = null;
    });
  }

  subscribe<TTopic extends keyof TopicDataMap>(
    topic: TTopic,
    keys: string[],
    handler: MessageHandler<TTopic>
  ): Subscription {
    const subscriptionKey = getKey(topic, keys);
    let internalSubscription = this.internalSubscriptions.get(subscriptionKey);
    const isNewSubscription = !internalSubscription;
    if (!internalSubscription) {
      internalSubscription = new InternalSubscription(
        topic,
        keys,
        this.unsubscribe.bind(this),
        this.internalSubscribe.bind(this)
      );
      this.internalSubscriptions.set(subscriptionKey, internalSubscription);
    }
    internalSubscription.addHandler(handler as BaseMessageHandler);
    if (isNewSubscription) {
      internalSubscription.subscribe();
    }
    const subscription: Subscription = {
      subscriptionKey,
      unsubscribe: () =>
        internalSubscription.removeHandler(handler as BaseMessageHandler),
    };
    return subscription;
  }

  //#endregion
}

let instance: ClientMessageService | null = null;

const getInstance = (): ClientMessageService => {
  return instance ?? connect();
};
const connect = () => {
  disconnect();
  instance = new ClientMessageService();
  instance.connect();
  return instance;
};
const disconnect = () => {
  instance?.disconnect();
  instance = null;
};

export const messageService = { connect, disconnect, getInstance };
